import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "./useAuth";

export interface ProfileData {
  description: string;
  skills: string[];
  photoUrl: string | null;
  full_name: string | null;
  headline: string | null;
  location: string | null;
  experience_level: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  instagram_url: string | null;
  is_admin: boolean;
  user_id: string | null;
  email_notifications_enabled: boolean;
}

async function fetchProfile(userId: string): Promise<ProfileData | null> {
  const { data: profileData, error } = await supabase
    .from('profiles')
    .select('full_name, logo_url, linkedin_url, location, is_admin, email_notifications_enabled')
    .eq('id', userId)
    .maybeSingle();

  if (error || !profileData) {
    if (error) console.error('Erro ao carregar perfil:', error);
    return null;
  }

  const { data: candidateData } = await supabase
    .from('candidates')
    .select('bio, skills, headline, experience_level, github_url, portfolio_url, instagram_url')
    .eq('profile_id', userId)
    .maybeSingle();

  return {
    description: candidateData?.bio || "",
    skills: Array.isArray(candidateData?.skills)
      ? candidateData.skills.map(skill => String(skill))
      : [],
    photoUrl: profileData.logo_url,
    full_name: profileData.full_name,
    headline: candidateData?.headline || null,
    location: profileData.location,
    experience_level: candidateData?.experience_level || null,
    linkedin_url: profileData.linkedin_url,
    github_url: candidateData?.github_url || null,
    portfolio_url: candidateData?.portfolio_url || null,
    instagram_url: candidateData?.instagram_url || null,
    is_admin: profileData.is_admin || false,
    user_id: userId,
    email_notifications_enabled: profileData.email_notifications_enabled ?? true,
  };
}

export function useProfile() {
  const queryClient = useQueryClient();
  const { data: user, isLoading: userLoading } = useAuthUser();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ['profile', userId],
    queryFn: () => fetchProfile(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: ['profile', userId] });
  };

  return {
    ...query,
    isLoading: userLoading || query.isLoading,
    profile: query.data,
    invalidateProfile,
  };
}
